import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { SalesHistoryContext } from '../SalesHistoryContext';
import { useProducts } from '../ProductContext';
import { apiJson } from '../apiClient';
import OverviewCommandPanel, { branchIdForApi, readOverviewBranchFilter } from '../components/OverviewCommandPanel';
import { useLanguage } from '../i18n/LanguageContext';
import { isLowStock } from '../utils/lowStock';
import { getLocalDateKey, dateKeyFromString, addDays } from '../utils/reportDates';
import { hasTenantModule } from '../utils/tenantModules';
import { canModerateBranch, canViewFinancials, firstDisplayNameFromUser, normalizeRole } from '../utils/userLocale';
import { tenantDisplayName } from '../utils/businessType';
import { requestExpensesSummaryPrefs, requestReportDailyDate, requestReportWeeklyForDate } from '../utils/navigationFocus';
import { slowStockMoversInWindow, topProductsInWindow } from '../utils/dashboardAnalytics';
import OverviewStatCards, { overviewMoneyValue, overviewDamagedStack } from '../components/OverviewStatCards';
import { useDamagedProducts } from '../DamagedProductsContext';
import './AutomotiveDashboardPage.css';

function buildLast7DayKeys() {
  const keys = [];
  const today = getLocalDateKey();
  for (let i = 6; i >= 0; i--) {
    keys.push(addDays(today, -i));
  }
  return keys;
}

function greetingForHour(t, date = new Date()) {
  const h = date.getHours();
  if (h < 12) return t('dash.greetingMorning');
  if (h < 17) return t('dash.greetingAfternoon');
  return t('dash.greetingEvening');
}

function formatTsh(value) {
  return Math.round(Number(value) || 0).toLocaleString();
}

function moneyValue(value) {
  return overviewMoneyValue(value);
}

function filterSalesByBranch(list, branchId) {
  if (!branchId || !list?.length) return list || [];
  return list.filter((s) => String(s.branchId ?? '') === String(branchId));
}

const AutomotiveDashboardPage = ({ user, onNavigate }) => {
  const { t } = useLanguage();
  const { products, inventoryLoading } = useProducts();
  const { damaged } = useDamagedProducts();
  const { salesHistory, salesLoading } = useContext(SalesHistoryContext);

  const isOwner = normalizeRole(user?.role) === 'owner';
  const canViewExpenses = canModerateBranch(user?.role);
  const showFinancials = canViewFinancials(user?.role);
  const hasExpensesModule = hasTenantModule(user, 'expenses');
  const hasReports = hasTenantModule(user, 'reports');
  const hasSubscription = hasTenantModule(user, 'subscription');
  const hasDebts = hasTenantModule(user, 'debts');

  const todayKey = getLocalDateKey();
  const weekKeys = useMemo(() => buildLast7DayKeys(), []);
  const firstName = firstDisplayNameFromUser(user);
  const businessName = tenantDisplayName(user);
  const tenant = user?.tenant || {};
  const sub = user?.subscription || {};

  const [branchId, setBranchId] = useState(() => (isOwner ? readOverviewBranchFilter() : ''));
  const [debtSummary, setDebtSummary] = useState({ openCount: 0, totalOwedTzs: 0 });
  const [expenseDay, setExpenseDay] = useState(0);
  const [expenseWeek, setExpenseWeek] = useState(0);

  const go = useCallback(
    (key) => {
      if (typeof onNavigate === 'function') onNavigate(key);
    },
    [onNavigate]
  );

  const apiBranchId = branchIdForApi(user, branchId);

  const salesToday = useMemo(() => {
    if (!salesHistory?.length) return [];
    const rows = salesHistory.filter((s) => dateKeyFromString(s.date) === todayKey);
    return filterSalesByBranch(rows, apiBranchId);
  }, [salesHistory, todayKey, apiBranchId]);

  const salesWeek = useMemo(() => {
    if (!salesHistory?.length) return [];
    const weekSet = new Set(weekKeys);
    const rows = salesHistory.filter((s) => weekSet.has(dateKeyFromString(s.date)));
    return filterSalesByBranch(rows, apiBranchId);
  }, [salesHistory, weekKeys, apiBranchId]);

  const salesTodayCount = salesToday.length;
  const salesTodayRevenue = useMemo(
    () => salesToday.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0),
    [salesToday]
  );
  const salesTodayProfit = useMemo(() => {
    return salesToday.reduce((sum, s) => {
      let profit = parseFloat(s.profit);
      if (Number.isNaN(profit)) {
        const amount = parseFloat(s.amount) || 0;
        const cost = parseFloat(s.cost) || 0;
        profit = amount - cost;
      }
      return sum + (Number.isFinite(profit) ? profit : 0);
    }, 0);
  }, [salesToday]);

  const salesWeekRevenue = useMemo(
    () => salesWeek.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0),
    [salesWeek]
  );

  const salesTrend = useMemo(() => {
    const rows = weekKeys.map((key) => {
      const total = salesWeek
        .filter((s) => dateKeyFromString(s.date) === key)
        .reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
      return { key, total };
    });
    const max = Math.max(1, ...rows.map((r) => r.total));
    return rows.map((r) => ({
      ...r,
      pct: Math.max(4, Math.round((r.total / max) * 100)),
      label: r.key.slice(5),
    }));
  }, [salesWeek, weekKeys]);

  const topMovers = useMemo(
    () => topProductsInWindow(salesWeek, weekKeys, 5),
    [salesWeek, weekKeys]
  );

  const slowMovers = useMemo(
    () => slowStockMoversInWindow(salesWeek, products, weekKeys, 5),
    [salesWeek, products, weekKeys]
  );

  const inventoryProducts = useMemo(
    () => (products || []).filter((p) => (Number(p.quantity) || 0) > 0),
    [products]
  );

  const totalProducts = products ? products.length : 0;
  const lowStock = products ? products.filter(isLowStock).length : 0;
  const categoriesTracked = useMemo(() => {
    if (!products?.length) return 0;
    return new Set(products.map((p) => String(p.category || '').trim()).filter(Boolean)).size;
  }, [products]);

  const damagedAccepted = damaged.filter((d) => d.status === 'approved').length;
  const damagedRejected = damaged.filter((d) => d.status === 'rejected').length;
  const damagedPending = damaged.filter((d) => d.status === 'pending').length;

  const trialActive = sub.status === 'trial' && sub.allowsAccess !== false;
  const daysLeft = sub.daysRemaining ?? sub.daysLeftInTrial ?? 0;
  const trialLengthDays = sub.trialLengthDays;

  useEffect(() => {
    if (!hasDebts || !showFinancials) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const qs = new URLSearchParams({ status: 'open' });
        if (isOwner && apiBranchId) qs.set('branchId', String(apiBranchId));
        const data = await apiJson(`/customer-debts?${qs.toString()}`);
        if (cancelled) return;
        setDebtSummary({
          openCount: data.summary?.openCount ?? 0,
          totalOwedTzs: data.summary?.totalOwedTzs ?? 0,
        });
      } catch {
        if (!cancelled) setDebtSummary({ openCount: 0, totalOwedTzs: 0 });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hasDebts, showFinancials, isOwner, apiBranchId]);

  useEffect(() => {
    if (!hasExpensesModule || !canViewExpenses) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const dayQs = new URLSearchParams({ window: 'day', date: todayKey });
        const weekQs = new URLSearchParams({ window: 'week', date: todayKey });
        const [dayData, weekData] = await Promise.all([
          apiJson(`/expenses/summary?${dayQs.toString()}`),
          apiJson(`/expenses/summary?${weekQs.toString()}`),
        ]);
        if (cancelled) return;
        const dayBuckets = dayData.buckets || {};
        const weekBuckets = weekData.buckets || {};
        const dayTotal = (Number(dayBuckets.boss) || 0) + (Number(dayBuckets.staff) || 0) + (Number(dayBuckets.office) || 0);
        const weekTotal = (Number(weekBuckets.boss) || 0) + (Number(weekBuckets.staff) || 0) + (Number(weekBuckets.office) || 0);
        setExpenseDay(Math.round((dayTotal + Number.EPSILON) * 100) / 100);
        setExpenseWeek(Math.round((weekTotal + Number.EPSILON) * 100) / 100);
      } catch {
        if (!cancelled) {
          setExpenseDay(0);
          setExpenseWeek(0);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hasExpensesModule, canViewExpenses, todayKey]);

  const todayNet = useMemo(
    () => Math.round((salesTodayProfit - expenseDay + Number.EPSILON) * 100) / 100,
    [salesTodayProfit, expenseDay]
  );

  const weekNet = useMemo(
    () => Math.round((salesWeekRevenue - expenseWeek + Number.EPSILON) * 100) / 100,
    [salesWeekRevenue, expenseWeek]
  );

  const quickActions = [
    { key: 'pos-sale-products', label: 'New Sale', icon: '▦', variant: 'green' },
    { key: 'orders-make', label: 'New Purchase', icon: '⊞', variant: 'slate', disabled: totalProducts === 0 },
    { key: 'stock-add', label: 'Add Product', icon: '▱', variant: 'blue' },
    { key: 'stock-list', label: 'Product List', icon: '☰', variant: 'teal' },
    { key: 'orders-tracking', label: 'Receive Stock', icon: '⇣', variant: 'amber', disabled: totalProducts === 0 },
    { key: 'pos-sale-history', label: 'Sales History', icon: '▤', variant: 'indigo' },
    ...(hasDebts ? [{ key: 'debts-list', label: 'Customer Debts', icon: '♙', variant: 'orange' }] : []),
    ...(showFinancials && hasReports
      ? [
          {
            key: 'report-daily',
            label: 'Reports',
            icon: '▥',
            variant: 'rose',
            preNavigate: () => requestReportDailyDate(todayKey),
          },
          {
            key: 'report-weekly',
            label: 'Weekly Report',
            icon: '▧',
            variant: 'cyan',
            preNavigate: () => requestReportWeeklyForDate(todayKey),
          },
        ]
      : []),
  ];

  const statCards = [
    {
      id: 'stock-items',
      title: 'Total stock items',
      value: totalProducts.toLocaleString(),
      caption: `${categoriesTracked.toLocaleString()} categories`,
      navKey: 'stock-list',
      border: 'purple',
      hint: 'All parts currently in stock catalog.',
    },
    {
      id: 'low-stock',
      title: 'Low stock',
      value: lowStock.toLocaleString(),
      caption: 'needs reorder',
      navKey: 'stock-alerts',
      border: 'yellow',
      hint: 'Items below safe stock level.',
    },
    {
      id: 'damaged',
      title: 'Damaged spare parts',
      value: overviewDamagedStack({
        accepted: damagedAccepted,
        rejected: damagedRejected,
        pending: damagedPending,
      }),
      navKey: 'damaged-products',
      border: 'red',
      hint: 'Record and review damaged spare parts.',
    },
    {
      id: 'sales-count',
      title: "Today's sales",
      value: salesTodayCount.toLocaleString(),
      caption: todayKey,
      navKey: 'pos-sale-history',
      border: 'green',
      hint: 'Number of transactions recorded today.',
    },
    {
      id: 'revenue',
      title: "Today's revenue",
      value: moneyValue(salesTodayRevenue),
      caption: todayKey,
      navKey: 'pos-sale-history',
      border: 'blue',
      hint: 'Gross revenue from POS today.',
      isMoney: true,
    },
    ...(showFinancials
      ? [
          {
            id: 'profit',
            title: "Today's profit",
            value: moneyValue(salesTodayProfit),
            caption: todayKey,
            navKey: 'report-daily',
            preNavigate: () => requestReportDailyDate(todayKey),
            border: 'teal',
            hint: 'Gross margin from today sales.',
            isMoney: true,
          },
        ]
      : []),
    ...(showFinancials && canViewExpenses
      ? [
          {
            id: 'net-today',
            title: "Today's net profit",
            value: moneyValue(todayNet),
            caption: todayKey,
            navKey: 'expenses-summary',
            preNavigate: () => requestExpensesSummaryPrefs({ window: 'day', anchorDate: todayKey }),
            border: 'slate',
            hint: 'Profit after daily expenses.',
            isMoney: true,
          },
          {
            id: 'net-week',
            title: 'This week net profit (rolling 7 days)',
            value: moneyValue(weekNet),
            caption: `${weekKeys[0]} → ${weekKeys[6]}`,
            navKey: 'expenses-summary',
            preNavigate: () => requestExpensesSummaryPrefs({ window: 'week', anchorDate: todayKey }),
            border: 'purple',
            hint: '7-day net performance after expenses.',
            isMoney: true,
          },
        ]
      : []),
    ...(showFinancials && hasDebts
      ? [
          {
            id: 'debt-book',
            title: 'Open customer debts',
            value: moneyValue(debtSummary.totalOwedTzs),
            caption: debtSummary.openCount > 0 ? `${debtSummary.openCount} open` : 'No open debts',
            navKey: 'debts-list',
            border: 'orange',
            hint: 'Outstanding customer balances.',
            isMoney: true,
          },
        ]
      : []),
  ];

  return (
    <div className="automotive-dash page-bleed">
      <header className="automotive-dash__hero">
        <div className="automotive-dash__hero-inner">
          <div className="automotive-dash__badge-row">
            <span className="automotive-dash__badge">Automotive products</span>
          </div>
          <h1 className="automotive-dash__greeting">
            {greetingForHour(t)}, {firstName}
          </h1>
          <p className="automotive-dash__sub">
            <strong>Duka la spare za gari na piki piki</strong> - stock, sales, supplier orders, debts, and performance in one automotive overview.
          </p>
        </div>
      </header>

      {trialActive ? (
        <div className="automotive-dash__trial" role="status">
          <strong>Free trial - full access</strong>
          {daysLeft > 0 ? (
            <span>
              {' '}
              - <strong>{daysLeft}</strong> days left
              {trialLengthDays ? ` (${trialLengthDays}-day trial)` : ''}.
            </span>
          ) : (
            <span> - trial ending soon. Upgrade to keep access.</span>
          )}
          {hasSubscription ? (
            <button type="button" className="automotive-dash__trial-link" onClick={() => go('subscription')}>
              View plans & upgrade
            </button>
          ) : null}
        </div>
      ) : null}

      {isOwner ? (
        <OverviewCommandPanel user={user} onNavigate={go} branchId={branchId} onBranchChange={setBranchId} />
      ) : null}

      <OverviewStatCards
        cards={statCards}
        onCardClick={(c) => {
          if (typeof c.preNavigate === 'function') c.preNavigate();
          go(c.navKey);
        }}
      />

      <div className="automotive-dash__overview-grid">
        <section className="automotive-dash__panel automotive-dash__quick" aria-label="Quick actions">
          <div className="automotive-dash__section-head">
            <h2 className="automotive-dash__section-title">Quick actions</h2>
          </div>
          <div className="automotive-dash__quick-grid">
            {quickActions.map((a) => (
              <button
                key={a.key}
                type="button"
                className={`automotive-dash__quick-card automotive-dash__quick-card--${a.variant}${a.disabled ? ' automotive-dash__quick-card--disabled' : ''}`}
                disabled={a.disabled}
                onClick={() => {
                  if (a.disabled) return;
                  if (typeof a.preNavigate === 'function') a.preNavigate();
                  go(a.key);
                }}
              >
                <span className="automotive-dash__quick-icon" aria-hidden>{a.icon}</span>
                <span className="automotive-dash__quick-label">{a.label}</span>
              </button>
            ))}
          </div>
        </section>

        <section className="automotive-dash__panel automotive-dash__kpi-chart" aria-label="Sales trend">
          <div className="automotive-dash__section-head">
            <div>
              <h2 className="automotive-dash__section-title">Sales trend</h2>
              <p className="automotive-dash__section-sub">Last 7 days counter sales</p>
            </div>
            <strong className="automotive-dash__chart-total">TZS {formatTsh(salesWeekRevenue)}</strong>
          </div>
          <div className="automotive-dash__bar-chart">
            {salesTrend.map((day) => (
              <div key={day.key} className="automotive-dash__bar-item">
                <span className="automotive-dash__bar-value">TZS {formatTsh(day.total)}</span>
                <span className="automotive-dash__bar-track">
                  <span className="automotive-dash__bar-fill" style={{ height: `${day.pct}%` }} />
                </span>
                <span className="automotive-dash__bar-label">{day.label}</span>
              </div>
            ))}
          </div>
          <div className="automotive-dash__kpi-row">
            <span>Transactions: <strong>{salesWeek.length.toLocaleString()}</strong></span>
            {showFinancials ? <span>Net week: <strong>TZS {formatTsh(weekNet)}</strong></span> : null}
          </div>
        </section>
      </div>

      <div className="automotive-dash__movers-grid">
        <section className="automotive-dash__panel" aria-label="Top movers">
          <div className="automotive-dash__section-head">
            <div>
              <h2 className="automotive-dash__section-title">Top movers</h2>
              <p className="automotive-dash__section-sub">Best-selling parts — last 7 days</p>
            </div>
          </div>
          {salesLoading && !salesWeek.length ? (
            <p className="automotive-dash__empty">Loading sales data…</p>
          ) : !topMovers.some((p) => p.qty > 0) ? (
            <p className="automotive-dash__empty">No sales in the last 7 days yet.</p>
          ) : (
            <ul className="automotive-dash__movers-list">
              {topMovers.map((p, idx) => {
                const maxQ = topMovers[0]?.qty || 1;
                const pct = Math.max(8, Math.round((p.qty / maxQ) * 100));
                return (
                  <li key={`top-${p.name}-${idx}`} className="automotive-dash__movers-row">
                    <span className="automotive-dash__movers-rank">{idx + 1}</span>
                    <div className="automotive-dash__movers-body">
                      <span className="automotive-dash__movers-name">{p.name}</span>
                      <span className="automotive-dash__movers-track" aria-hidden="true">
                        <span className="automotive-dash__movers-fill automotive-dash__movers-fill--top" style={{ width: `${pct}%` }} />
                      </span>
                    </div>
                    <span className="automotive-dash__movers-qty">{p.qty} sold</span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section className="automotive-dash__panel" aria-label="Slow movers">
          <div className="automotive-dash__section-head">
            <div>
              <h2 className="automotive-dash__section-title">Slow movers</h2>
              <p className="automotive-dash__section-sub">In stock but zero sales — last 7 days</p>
            </div>
          </div>
          {inventoryLoading && !inventoryProducts.length ? (
            <p className="automotive-dash__empty">Loading inventory…</p>
          ) : !slowMovers.length ? (
            <p className="automotive-dash__empty">
              {inventoryProducts.length
                ? 'No slow movers — every stocked part had at least one sale this week.'
                : 'No inventory yet. Add opening stock or receive a purchase first.'}
            </p>
          ) : (
            <ul className="automotive-dash__movers-list">
              {slowMovers.map((p, idx) => {
                const maxS = slowMovers[0]?.stockQty || 1;
                const pct = Math.max(8, Math.round((p.stockQty / maxS) * 100));
                return (
                  <li key={`slow-${p.id}-${idx}`} className="automotive-dash__movers-row">
                    <span className="automotive-dash__movers-rank automotive-dash__movers-rank--slow">{idx + 1}</span>
                    <div className="automotive-dash__movers-body">
                      <span className="automotive-dash__movers-name">{p.name}</span>
                      <span className="automotive-dash__movers-track" aria-hidden="true">
                        <span className="automotive-dash__movers-fill automotive-dash__movers-fill--slow" style={{ width: `${pct}%` }} />
                      </span>
                    </div>
                    <span className="automotive-dash__movers-qty">{p.stockQty} in stock</span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>

      <section className="automotive-dash__panel automotive-dash__panel--wide" aria-label="Recent sales">
        <div className="automotive-dash__section-head">
          <h2 className="automotive-dash__section-title">Recent sales</h2>
          <p className="automotive-dash__section-sub">{todayKey}</p>
        </div>

        {inventoryLoading || (salesLoading && !salesToday.length) ? (
          <p className="automotive-dash__empty">Loading dashboard activity...</p>
        ) : salesToday.length === 0 ? (
          <>
            <p className="automotive-dash__empty">No sales recorded for today yet. Start selling from POS.</p>
            <button type="button" className="automotive-dash__empty-cta" onClick={() => go('pos-sale-products')}>
              Open POS
            </button>
          </>
        ) : (
          <div className="automotive-dash__table-wrap">
            <table className="automotive-dash__table">
              <thead>
                <tr>
                  <th>Time/Date</th>
                  <th>Product</th>
                  <th>Qty</th>
                  <th>Payment</th>
                  <th>Amount (TZS)</th>
                </tr>
              </thead>
              <tbody>
                {salesToday.slice(0, 8).map((s, i) => (
                  <tr key={s.id != null ? String(s.id) : `sale-${i}`}>
                    <td>{s.date || '-'}</td>
                    <td>{s.product?.name || '-'}</td>
                    <td>{s.quantity ?? '-'}</td>
                    <td>{s.paymentLabel || s.paymentMethod || '-'}</td>
                    <td>{s.amount != null ? formatTsh(s.amount) : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default AutomotiveDashboardPage;
